import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getSuiteExecutionByRunNumber } from '@/features/executions/data';
import { getTestExecutionByRunNumber } from '@/features/executions/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useAccessToken } from '@/shared/hooks';
import { executionKeys } from '../queries/executionsQueryKeys';
import type { ApiError } from '@/shared/data/client';

export const useSuiteExecutionDetailQuery = (
  suiteSlug: string | null,
  runNumber: number | null,
  projectId?: string | null,
): UseQueryResult<Awaited<ReturnType<typeof getSuiteExecutionByRunNumber>> | null, ApiError> => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const projectKey = projectId ?? null;
  return useQuery<Awaited<ReturnType<typeof getSuiteExecutionByRunNumber>> | null, ApiError>({
    queryKey: executionKeys.suiteExecutionRunDetail(suiteSlug, runNumber, projectKey, orgKey),
    enabled: Boolean(token && suiteSlug && runNumber !== null),
    queryFn: async (): Promise<Awaited<ReturnType<typeof getSuiteExecutionByRunNumber>> | null> => {
      return await getSuiteExecutionByRunNumber(
        suiteSlug,
        runNumber,
        projectId ?? null,
        selectedOrganisationId ?? undefined,
      );
    },
    staleTime: 15_000,
  });
};

export const useTestExecutionDetailQuery = (
  testSlug: string | null,
  runNumber: number | null,
  projectId?: string | null,
): UseQueryResult<Awaited<ReturnType<typeof getTestExecutionByRunNumber>> | null, ApiError> => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const projectKey = projectId ?? null;
  return useQuery<Awaited<ReturnType<typeof getTestExecutionByRunNumber>> | null, ApiError>({
    queryKey: executionKeys.testExecutionRunDetail(testSlug, runNumber, projectKey, orgKey),
    enabled: Boolean(token && testSlug && runNumber !== null),
    queryFn: async (): Promise<Awaited<ReturnType<typeof getTestExecutionByRunNumber>> | null> => {
      return await getTestExecutionByRunNumber(
        testSlug,
        runNumber,
        projectId ?? null,
        selectedOrganisationId ?? undefined,
      );
    },
    staleTime: 15_000,
  });
};
